/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import type { FilterValue, RefinementProps } from '../types';
import { compareSizeValues } from './sort';

export default function RefineSize({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
}: RefinementProps): ReactElement {
    const sorted = useMemo(
        () =>
            [...values].sort((a: FilterValue, b: FilterValue) =>
                compareSizeValues(String(a.label || a.value), String(b.label || b.value))
            ),
        [values]
    );

    return (
        /* SIZE IS A CHECKBOX LIST, ONE VALUE PER ROW — the same control every
           other facet group uses, which is what live draws on both brands.
           Measured off the owner's 2026-08-24 captures, the first on either
           brand with the group EXPANDED (every earlier capture had it
           collapsed, rendering no children, which is why this was carried as
           unmeasured for so long). DPR 2 derived per file from a hairline
           border landing on exactly 2 device pixels:

             live NM  PLP, 812 CSS   16.0px box, 36.0 CSS row pitch, 11.0 gap
             live BG  SRP, 1157 CSS  14.0px box, 39.0 CSS row pitch, 10.5 gap

           This component does NOT set those per-brand geometries. Row padding,
           checkbox size and gap are shared with `refine-default` so Size reads
           as the same control as the groups beside it; if that shared geometry
           needs a per-brand pass, it belongs in the token overlay, not here.

           WHAT REPLACED WHAT: this was a `flex-wrap` grid of outline Button
           pills, 2–4 per row. That is a different CONTROL, not a spacing
           difference, and it is what the owner reported on both brands.

           THE CAP STAYS ON THE LIST, not on the Radix content div above it —
           that div carries an inline height driving the open/close animation,
           so a max-height there fights the animation instead of bounding the
           list. Size keeps its own token rather than borrowing the general
           facet one; evidence for both names is in the brand token files.
           Live BG corroborates that a cap exists: its expanded SIZE group
           paints a 5x26 CSS scroll thumb in rgb(237,237,237) with the track
           at the top of its travel, over a value list ~288 CSS tall between
           the group's own rgb(211,211,211) hairlines. */
        <div className="space-y-1 mt-2 max-h-[var(--facet-size-list-max-height,none)] overflow-y-auto">
            {sorted.map((value, idx) => {
                const id = `refine-${attributeId}-${idx}`;
                const isSelected = isFilterSelected(attributeId, value.value);

                return (
                    <label
                        key={`${attributeId}:${value.value}`}
                        htmlFor={id}
                        className="flex items-center p-2 hover:bg-muted/30 cursor-pointer">
                        <Checkbox
                            id={id}
                            checked={isSelected}
                            onCheckedChange={() => toggleFilter(attributeId, value.value)}
                            className="size-4"
                        />
                        {/* NO PER-VALUE COUNT ON SIZE, ON EITHER BRAND, and this
                            is the one place this component deliberately differs
                            from a live capture — read this before adding it
                            back.

                            LIVE NM DRAWS NO COUNT HERE. Zero of the twenty
                            values in its expanded SIZE group carry one, against
                            a control of the same page's Category group, which
                            does. So NM is measured, and dropping the count is
                            simply parity.

                            LIVE BG DRAWS ONE — `0-3 Months (5)`, `1.5 (16)`,
                            `2 (17)` … all seven visible values. That reading is
                            good and it is NOT the state our catalogue is in.
                            Every size value live BG shows there is free of its
                            own parentheses. Ours are not: a large minority of
                            this attribute is a dual-size catalogue label
                            carrying a bracketed equivalent, and appending a
                            parenthesised count to one produces two adjacent
                            bracketed numbers meaning different things.

                            Measured over the running app, not sampled — SCAPI
                            `c_size` across eleven queries per brand: 10 of 57
                            NM values and 34 of 79 BG values carry their own
                            parens, and of those, 10 NM and 18 BG hold a BARE
                            NUMBER inside, which is the ambiguous case. It
                            rendered as `38 (2)(1)` sitting directly beside
                            `38 (3)` on NM, and as `24 (0)(1)`, `31 (10)(1)`,
                            `8 US (2)(1)` on BG. A one-query sample said BG was
                            clean; widening the population refuted that, so do
                            not re-derive this from a single category.

                            SO: live BG's rule is faithful to a state we are not
                            in, and applying it reproduces an ambiguity live
                            does not have. The count is dropped on both brands.
                            The count itself is low-value on a size filter, and
                            it is the only thing creating the collision.

                            THE REVERSAL IS ONE EDIT, and it is worth taking the
                            day the catalogue stops shipping bracketed dual-size
                            labels: restore the nested count span exactly as
                            `refine-default` draws it, BG only. Do not restore
                            it on NM — that one is measured absent. And do not
                            "fix" this by changing the count's delimiter or
                            colour instead: no live source on either brand uses
                            a separator this component could borrow, so that
                            invents a format rather than matching one. */}
                        <span className="type-label-medium ml-3">{value.label || value.value}</span>
                    </label>
                );
            })}
        </div>
    );
}
